"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateActivityLogQueries } from "@/app/hooks/settings/activityLogQueryKeys";
import { impersonateService } from "@/app/services/impersonateService";
import { getOriginalAdmin, getUser, getToken, setUser, clearOriginalAdmin } from "@/app/services/utils";
import { authService } from "@/app/services/authService";
import type { User } from "@/app/types/user";
import type { ImpersonationSurface } from "@/app/types/impersonationErrors";

/**
 * Safely serializes an object for logging, handling circular references and non-serializable values
 */
function safeSerializeForLogging(obj: any): any {
  try {
    return JSON.parse(JSON.stringify(obj, (key, value) => {
      if (value instanceof Error) {
        return {
          name: value.name,
          message: value.message,
          stack: value.stack,
        };
      }
      if (typeof value === "function") {
        return `[Function: ${value.name || "anonymous"}]`;
      }
      if (value === undefined) {
        return "[undefined]";
      }
      if (typeof value === "symbol") {
        return value.toString();
      }
      return value;
    }));
  } catch (e) {
    return {
      _serializationError: "Failed to serialize object",
      _toString: String(obj),
    };
  }
}

// Status cache configuration
const STATUS_CACHE_KEY = '__impersonation_status_cache';
const STATUS_CACHE_TTL = 30000; // 30 seconds

interface CachedStatus {
  isImpersonating: boolean;
  impersonatedUser: User | null;
  originalAdmin: User | null;
  timestamp: number;
}

// Single source of truth for redirect state
// Using a simple flag that's cleared on page load
let isRedirecting = false;
let redirectTimeout: NodeJS.Timeout | null = null;
let checkStatusInProgress = false;

// Helper to check if redirect is in progress
function isRedirectInProgress(): boolean {
  return isRedirecting;
}

// Helper to set redirect state
function setRedirectInProgress(): void {
  isRedirecting = true;
}

// Helper to clear redirect state
function clearRedirectInProgress(): void {
  isRedirecting = false;
  if (redirectTimeout) {
    clearTimeout(redirectTimeout);
    redirectTimeout = null;
  }
}

// Status cache helpers
function getCachedStatus(): CachedStatus | null {
  if (typeof window === 'undefined') return null;
  try {
    const cached = localStorage.getItem(STATUS_CACHE_KEY);
    if (!cached) return null;
    const parsed: CachedStatus = JSON.parse(cached);
    const now = Date.now();
    if (now - parsed.timestamp > STATUS_CACHE_TTL) {
      localStorage.removeItem(STATUS_CACHE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function setCachedStatus(status: Omit<CachedStatus, 'timestamp'>): void {
  if (typeof window === 'undefined') return;
  try {
    const cached: CachedStatus = {
      ...status,
      timestamp: Date.now(),
    };
    localStorage.setItem(STATUS_CACHE_KEY, JSON.stringify(cached));
  } catch {
    // Ignore cache errors
  }
}

function clearCachedStatus(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STATUS_CACHE_KEY);
}

export function useImpersonation() {
  const queryClient = useQueryClient();
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState<User | null>(null);
  const [originalAdmin, setOriginalAdmin] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // AbortController to cancel pending checkStatus calls
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check impersonation status on mount and when needed
  const checkStatus = useCallback(async (signal?: AbortSignal, useCache = true) => {
    // Don't check status if we're redirecting
    if (isRedirectInProgress()) {
      return;
    }
    
    // Check cache first if enabled
    if (useCache) {
      const cached = getCachedStatus();
      if (cached) {
        setIsImpersonating(cached.isImpersonating);
        setImpersonatedUser(cached.impersonatedUser);
        setOriginalAdmin(cached.originalAdmin);
        setIsLoading(false);
        return;
      }
    }
    
    // Prevent multiple simultaneous calls
    if (checkStatusInProgress) {
      return;
    }
    
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    const abortSignal = signal || controller.signal;
    
    checkStatusInProgress = true;
    
    try {
      setIsLoading(true);
      const status = await impersonateService.getImpersonationStatus();
      
      // Check if aborted or redirecting before updating state
      if (abortSignal.aborted || isRedirectInProgress()) {
        return;
      }
      
      const isImpersonating = status.isImpersonating || false;
      let impersonatedUser: User | null = null;
      let originalAdmin: User | null = null;
      
      if (isImpersonating) {
        impersonatedUser = status.impersonatedUser || null;
        // Get original admin from localStorage or from status
        const storedAdmin = getOriginalAdmin();
        if (storedAdmin) {
          originalAdmin = storedAdmin;
        } else if (status.originalAdminId) {
          // If we have the ID but not the full object, try to get it from current user
          const currentUser = authService.getStoredUser();
          if (currentUser?.originalAdmin) {
            originalAdmin = currentUser.originalAdmin;
          }
        }
      }
      
      // Update state
      setIsImpersonating(isImpersonating);
      setImpersonatedUser(impersonatedUser);
      setOriginalAdmin(originalAdmin);
      
      // Cache the result
      setCachedStatus({
        isImpersonating,
        impersonatedUser,
        originalAdmin,
      });
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }
      console.error("[useImpersonation] Error checking status:", error);
      // Only update state if not redirecting and not aborted
      if (!abortSignal.aborted && !isRedirectInProgress()) {
        setIsImpersonating(false);
        setImpersonatedUser(null);
        setOriginalAdmin(null);
      }
    } finally {
      checkStatusInProgress = false;
      if (!abortSignal.aborted && !isRedirectInProgress()) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // Clear redirect flag on mount (redirect completed if we're here)
    clearRedirectInProgress();
    
    // Check from stored user data first (synchronous, immediate)
    const storedUser = authService.getStoredUser();
    if (storedUser?.isImpersonated) {
      setIsImpersonating(true);
      setImpersonatedUser(storedUser);
      if (storedUser.originalAdmin) {
        setOriginalAdmin(storedUser.originalAdmin);
      } else {
        const storedAdmin = getOriginalAdmin();
        if (storedAdmin) {
          setOriginalAdmin(storedAdmin);
        }
      }
      setIsLoading(false);
    } else {
      setIsImpersonating(false);
      setImpersonatedUser(null);
      setOriginalAdmin(null);
      setIsLoading(false);
    }
    
    // Then check status from API (async, but don't block)
    checkStatus();
    
    // Cleanup function to abort pending requests
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, [checkStatus]); // Include checkStatus in dependencies

  const startImpersonation = useCallback(
    async (userId: number, options?: { surface?: ImpersonationSurface }) => {
      // Prevent multiple simultaneous impersonation attempts
      if (isRedirectInProgress()) {
        console.warn("[useImpersonation] Redirect already in progress, ignoring start request");
        return;
      }
      
      try {
        // Cancel any pending checkStatus calls
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
          abortControllerRef.current = null;
        }
        
        // Clear any existing redirect timeout
        if (redirectTimeout) {
          clearTimeout(redirectTimeout);
          redirectTimeout = null;
        }
        
        // Set redirect flag and clear cache
        setRedirectInProgress();
        clearCachedStatus();
        setIsLoading(true);
        
        const result = await impersonateService.startImpersonation(userId, options);
        void invalidateActivityLogQueries(queryClient);

        if (!getToken()) {
          console.error("[useImpersonation] Token was not saved correctly!");
          throw new Error("Failed to save authentication token");
        }
        
        // Clear React state before redirect
        setIsImpersonating(false);
        setImpersonatedUser(null);
        setOriginalAdmin(null);
        
        // Determine redirect URL based on user role
        const redirectUrl = result.user.role?.id === 6 
          ? "/my/profile"  // Client - redirect to their profile
          : "/dashboard";   // Other users - redirect to dashboard
        
        // Perform redirect with minimal delay
        redirectTimeout = setTimeout(() => {
          redirectTimeout = null;
          window.location.href = redirectUrl;
        }, 100);
        
        return result;
      } catch (error) {
        // Properly serialize error for logging
        let errorDetails: any = {
          userId,
        };
        
        if (error instanceof Error) {
          errorDetails.error = error.message;
          errorDetails.errorName = error.name;
          // Include structured error data if available
          if ("errorCode" in error) {
            errorDetails.errorCode = (error as any).errorCode;
          }
          if ("details" in error) {
            errorDetails.errorDetails = (error as any).details;
          }
          if ("status" in error) {
            errorDetails.status = (error as any).status;
          }
        } else if (typeof error === "object" && error !== null) {
          errorDetails.error = (error as any).message || (error as any).error || String(error);
          if ("errorCode" in error) {
            errorDetails.errorCode = (error as any).errorCode;
          }
        } else {
          errorDetails.error = String(error);
        }
        
        const serializedErrorDetails = safeSerializeForLogging(errorDetails);
        console.error("[useImpersonation] Error starting impersonation:", serializedErrorDetails);
        clearRedirectInProgress();
        setIsLoading(false);
        
        // Re-throw the error so component can handle it
        throw error;
      }
    },
    [queryClient]
  );

  const stopImpersonation = useCallback(async () => {
    // Prevent multiple simultaneous stop attempts
    if (isRedirectInProgress()) {
      console.warn("[useImpersonation] Redirect already in progress, ignoring stop request");
      return;
    }
    
    try {
      // Cancel any pending checkStatus calls
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      
      // Clear any existing redirect timeout
      if (redirectTimeout) {
        clearTimeout(redirectTimeout);
        redirectTimeout = null;
      }
      
      // Set redirect flag and clear cache
      setRedirectInProgress();
      clearCachedStatus();
      setIsLoading(true);
      
      const result = await impersonateService.stopImpersonation();
      void invalidateActivityLogQueries(queryClient);

      if (!getToken()) {
        console.error("[useImpersonation] Token was not saved correctly after stop!");
        throw new Error("Failed to save authentication token");
      }
      
      // Clear React state before redirect
      setIsImpersonating(false);
      setImpersonatedUser(null);
      setOriginalAdmin(null);
      
      // Perform redirect with minimal delay
      redirectTimeout = setTimeout(() => {
        redirectTimeout = null;
        window.location.href = "/dashboard";
      }, 100);
      
      return result;
    } catch (error) {
      const status = error && typeof error === "object" && "status" in error ? (error as { status: number }).status : undefined;
      const message = error instanceof Error ? error.message : String(error);

      // 401 or no token: session invalid – clear auth state and redirect to login (silent)
      if (status === 401 || message.includes("No authentication token")) {
        console.log("[useImpersonation] Auth invalid, clearing state and redirecting to login");
        authService.logout();
        clearCachedStatus();
        setIsImpersonating(false);
        setImpersonatedUser(null);
        setOriginalAdmin(null);
        clearRedirectInProgress();
        setIsLoading(false);
        if (typeof window !== "undefined") {
          window.location.href = "/";
        }
        return;
      }

      // 400 "Not currently impersonating": stale state – clear impersonation data locally
      if (status === 400 || message.toLowerCase().includes("not currently impersonating")) {
        console.log("[useImpersonation] Stale impersonation state, clearing locally");
        clearOriginalAdmin();
        clearCachedStatus();
        const storedUser = getUser();
        if (storedUser && storedUser.isImpersonated) {
          setUser({ ...storedUser, isImpersonated: false });
        }
        setIsImpersonating(false);
        setImpersonatedUser(null);
        setOriginalAdmin(null);
        clearRedirectInProgress();
        setIsLoading(false);
        return;
      }

      // Other errors – log and rethrow for UI to handle
      const errorDetails: Record<string, unknown> = {
        error: message,
        status,
      };
      if (error instanceof Error && "errorCode" in error) {
        errorDetails.errorCode = (error as { errorCode?: unknown }).errorCode;
      }
      const serializedErrorDetails = safeSerializeForLogging(errorDetails);
      console.error("[useImpersonation] Error stopping impersonation:", serializedErrorDetails);
      clearRedirectInProgress();
      setIsLoading(false);
      throw error;
    }
  }, [queryClient]);

  return {
    isImpersonating,
    impersonatedUser,
    originalAdmin,
    startImpersonation,
    stopImpersonation,
    isLoading,
    checkStatus,
  };
}

